// Guide-level content for each destination: visa, packing, attractions and tips.
// Complements the records in destinations.js, which carry the atlas-level fields.

export const placeGuides = {
  'dubai': {
    id: 'dubai',
    name: 'Dubai, UAE',
    image: 'https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=1200&q=80',
    bestTime: 'November to March',
    currency: 'AED (Emirati Dirham)',
    language: 'Arabic, English widely spoken',
    climate: 'Hot and dry, 35-45°C in summer',
    tagline: 'Experience luxury, towering skyscrapers, and desert adventures',
    overview: 'Dubai represents the pinnacle of modern urban development, where futuristic skyscrapers rise from desert sands to create a breathtaking skyline. This ultra-modern city seamlessly blends Arabian tradition with cutting-edge innovation, offering visitors a unique experience. From the world-famous Burj Khalifa to lavish shopping malls, pristine beaches, and thrilling desert safaris, Dubai caters to every travel style. The city\'s diverse attractions include historic souks where you can haggle for gold and spices, luxurious resort hotels with Michelin-starred restaurants, and vibrant nightlife. Whether you\'re seeking adventure in the dunes, cultural immersion in traditional neighborhoods, or shopping in world-class malls, Dubai delivers an unforgettable experience. The city\'s multicultural atmosphere and world-class infrastructure make it exceptionally welcoming to tourists from around the globe.',
    visa: {
      summary: 'UAE offers visa-on-arrival for most nationalities. Indian and Pakistani nationals get 30-day visas automatically.',
      details: [
        'Citizens from 180+ countries can get visa-on-arrival',
        'Indian nationals get 30-day visa on arrival',
        'Passport valid for at least 6 months beyond stay',
        'Return ticket and hotel booking may be required',
        'Multiple entry visas available for longer stays'
      ],
      note: 'Apply online through ICP (General Directorate of Residency) for express processing'
    },
    packing: {
      'Clothing': ['Lightweight, breathable clothing', 'Sun hat and sunglasses', 'Light scarf or cardigan (for air-conditioning)', 'Swimwear', 'Formal wear for evenings'],
      'Accessories': ['High SPF sunscreen (50+)', 'Reusable water bottle', 'Small crossbody bag', 'Comfortable walking shoes', 'Sandals'],
      'Electronics': ['Phone charger', 'Universal adapter', 'Portable power bank', 'Camera']
    },
    attractions: [
      {
        name: 'Burj Khalifa',
        description: 'The world\'s tallest building with stunning views of Dubai and the Arabian Gulf from the observation decks.',
        tip: 'Book tickets online in advance and visit at sunset for spectacular views'
      },
      {
        name: 'Palm Jumeirah',
        description: 'The famous man-made palm-shaped island with luxury resorts, pristine beaches, and water sports.',
        tip: 'Take the monorail for scenic views of the entire palm structure'
      },
      {
        name: 'Gold Souk',
        description: 'Historic market with thousands of gold shops offering the finest jewelry from around the world.',
        tip: 'Haggle respectfully and buy gold by weight, not design'
      },
      {
        name: 'Desert Safari',
        description: 'Experience the Arabian desert with dune bashing, camel rides, and traditional Bedouin camps.',
        tip: 'Go in the evening for sunset and cooler temperatures'
      },
      {
        name: 'Dubai Mall',
        description: 'One of the largest shopping malls in the world with 1200+ stores, restaurants, and an indoor aquarium.',
        tip: 'Visit early morning or late evening to avoid crowds'
      }
    ],
    tips: [
      'Dress modestly, especially outside tourist areas',
      'Friday and Saturday are weekend holidays',
      'Alcohol is available in licensed venues and hotels',
      'Tipping is customary (10-15%) but not mandatory',
      'Cash and cards are widely accepted',
      'Public transportation via metro is efficient and affordable',
      'Stay hydrated - carry water bottles always',
      'Air-conditioning is everywhere, bring a light jacket'
    ],
    faqs: [
      {
        question: 'How many days do you need in Dubai?',
        answer: 'Four to five days is enough to see the Burj Khalifa, Old Dubai, a desert safari and still have a beach day. Add two more if you want to include Abu Dhabi and the Louvre. Anything under three days means rushing between attractions that sit far apart.'
      },
      {
        question: 'What is the best time to visit Dubai?',
        answer: 'November to March, when daytime temperatures sit around 24-30°C and outdoor dining, beaches and desert trips are comfortable. June to August regularly crosses 45°C, which confines you to air-conditioned malls for most of the day, though hotel rates drop sharply.'
      },
      {
        question: 'Is Dubai expensive to visit?',
        answer: 'It scales to your choices more than most cities. Public beaches, the metro, Old Dubai souks and Deira eateries cost very little, while Marina brunches, resort stays and theme parks add up fast. Mixing one splurge experience with otherwise modest spending keeps a Dubai trip reasonable.'
      },
      {
        question: 'What should I wear in Dubai?',
        answer: 'Cover shoulders and knees in malls, souks, government buildings and residential areas. Swimwear is fine at hotel pools, beach clubs and public beaches. Mosques require women to cover their hair, and abayas are usually lent at the entrance.'
      },
      {
        question: 'Is Dubai good for a first international trip?',
        answer: 'Yes. English is spoken almost everywhere, the metro is simple, tap-to-pay works widely, flights from India are short and frequent, and Indian food is available in every price bracket. It is one of the gentlest introductions to travelling abroad.'
      },
      {
        question: 'How much does a Dubai trip cost from India?',
        answer: 'Dubai scales to your choices more than almost anywhere. Accommodation and paid attractions drive the total; food, the metro and public beaches barely move it. The same four days can be modest or extravagant depending on whether you stay in Deira or the Marina, and on how many ticketed attractions you book.'
      },
      {
        question: 'When is the cheapest time to visit Dubai?',
        answer: 'June to August, when temperatures pass 45\u00b0C and hotel rates fall steeply. You trade comfort for value and spend most of the day indoors. The shoulder months of April, May and September are the better compromise, still warm but far cheaper than the November to March peak.'
      },
      {
        question: 'How long is the flight from India to Dubai?',
        answer: 'Around three hours from Mumbai and three and a half from Delhi, with direct flights from most major Indian cities including Bengaluru, Chennai, Hyderabad and Kochi. The frequency is high enough that fares vary more by season and booking window than by route.'
      },
      {
        question: 'What costs more than expected in Dubai?',
        answer: 'Alcohol, which carries heavy duty and is limited to licensed venues; taxis over longer distances, since the city is far more spread out than it looks; and theme park or observation deck tickets, which are priced per attraction and add up quickly if you book several.'
      }
    ]
  },
  'thailand': {
    id: 'thailand',
    name: 'Thailand',
    image: 'https://images.unsplash.com/photo-1504194104404-433180773017?w=1200&q=80',
    bestTime: 'November to February',
    currency: 'Thai Baht (THB)',
    language: 'Thai, English in tourist areas',
    climate: 'Tropical, 25-35°C, variable humidity',
    tagline: 'Discover ancient temples, pristine beaches, and world-famous Thai cuisine',
    overview: 'Thailand captivates visitors with its extraordinary blend of spiritual heritage, tropical paradise, and culinary excellence. This Southeast Asian gem offers something for every traveler—from the chaotic energy of Bangkok with its floating markets and ornate temples to peaceful island escapes with crystal-clear waters and limestone cliffs. Thailand\'s rich Buddhist culture permeates daily life, evident in thousands of golden temples and respectful traditions. The country\'s famous hospitality and welcoming spirit make visitors feel immediately at home. World-class beaches in Phuket, Krabi, and the Andaman Islands provide perfect relaxation, while cultural heartlands like Chiang Mai offer temple visits, cooking classes, and authentic interactions with locals. Thai cuisine—renowned globally for its perfect balance of sweet, sour, salty, and spicy—is an adventure in itself. Whether exploring ancient ruins, meditating in temples, diving in turquoise waters, or simply embracing the "Land of Smiles" philosophy, Thailand creates memories that last a lifetime.',
    visa: {
      summary: 'Indian passport holders can enter Thailand without a visa for tourist stays of up to 60 days. There is nothing to apply for and no fee.',
      details: [
        'This is a visa exemption, not a visa on arrival, so there is nothing to buy at the airport',
        'Up to 60 days is granted free of charge for tourism',
        'Stays longer than 60 days need a tourist visa applied for in advance at thaievisa.go.th',
        'A stay can also be extended through the Immigration Bureau once you are in the country',
        'Passport valid at least six months, with proof of onward travel and accommodation',
        'Complete the Thailand Digital Arrival Card before you travel'
      ],
      note: 'Confirmed against the Ministry of Foreign Affairs eligibility checker at thaievisa.go.th. Entry rules change, so re-check before booking.'
    },
    packing: {
      'Clothing': ['Lightweight cotton clothing', 'Rain jacket or poncho', 'Swim wear', 'Loose-fitting modest clothes', 'Comfortable walking shoes', 'Flip-flops'],
      'Accessories': ['High SPF sunscreen', 'Hat and sunglasses', 'Reusable water bottle', 'Waterproof bag', 'Bug spray'],
      'Electronics': ['Phone charger', 'Universal adapter', 'Portable power bank', 'Underwater camera case']
    },
    attractions: [
      {
        name: 'Angkor Wat, Cambodia',
        description: 'The largest religious monument in the world, a UNESCO World Heritage site with stunning architecture.',
        tip: 'Hire a guide and start before sunrise for best photos'
      },
      {
        name: 'Grand Palace & Temple of the Emerald Buddha',
        description: 'Bangkok\'s most sacred temple complex with intricate architecture and spiritual significance.',
        tip: 'Dress respectfully - covered shoulders and knees required'
      },
      {
        name: 'Phi Phi Islands',
        description: 'Paradise islands with crystal-clear waters, white-sand beaches, and incredible snorkeling.',
        tip: 'Visit Maya Bay early morning to beat cruise ship crowds'
      },
      {
        name: 'Floating Markets',
        description: 'Traditional markets where vendors sell fresh produce and souvenirs from wooden boats.',
        tip: 'Visit Damnoen Saduak or Amphawa early morning for authentic experience'
      },
      {
        name: 'Thai Cooking Class',
        description: 'Learn to prepare authentic Thai dishes from professional chefs in local cooking schools.',
        tip: 'Book classes in advance, most include market visit and meal'
      }
    ],
    tips: [
      'Respect the Thai monarchy - it\'s a serious offense to disrespect the King',
      'Wai (hands together) is a greeting - reciprocate politely',
      'Eat from street vendors - food is fresh, affordable, and delicious',
      'Stay hydrated and use sunscreen generously',
      'Use trusted taxis with meters or Grab app',
      'Bargain respectfully in markets',
      'Most places accept cards, but carry cash for street food',
      'Temple etiquette: remove shoes, dress modestly, speak quietly'
    ],
    faqs: [
      {
        question: 'How many days do you need in Thailand?',
        answer: 'Ten to fourteen days lets you pair Bangkok with one island region and either Chiang Mai or a national park. Seven days works if you pick just Bangkok plus one beach. Trying to fit north, south and the capital into a week means spending most of it in transit.'
      },
      {
        question: 'What is the best time to visit Thailand?',
        answer: 'November to February is the cool dry season and the most comfortable for sightseeing. The catch is that the two coasts run on opposite schedules: the Andaman side around Phuket and Krabi is best November to April, while the Gulf islands like Koh Samui stay drier through June to September.'
      },
      {
        question: 'Which Thai islands should a first-timer choose?',
        answer: 'Krabi and Koh Lanta suit travellers who want beaches with some quiet, Phuket suits those who want everything on tap, and Koh Samui or Koh Phangan suit anyone travelling in the Andaman monsoon. Pick one cluster rather than island-hopping across both coasts.'
      },
      {
        question: 'Is Thailand cheap to travel in?',
        answer: 'It remains one of the better value destinations in the region. Street food, local transport and guesthouses cost very little, while island transfers, resorts and organised tours are where budgets actually go. Islands are consistently more expensive than the mainland.'
      },
      {
        question: 'Is Thailand a good first trip abroad?',
        answer: 'Yes. Signage is bilingual, tourist infrastructure is mature, domestic flights are cheap and frequent, and vegetarian food is easy once you learn to ask for it without fish sauce. It is a common first international trip for Indian travellers for good reason.'
      },
      {
        question: 'How much does a Thailand trip cost from India?',
        answer: 'Three things decide the total: flights, whether you add islands, and how often you move between regions. Bangkok and Chiang Mai are inexpensive day to day, the islands are consistently the costliest part, and transfers between them add up faster than most people budget for. The same itinerary in June can cost far less than in December.'
      },
      {
        question: 'When is the cheapest time to visit Thailand?',
        answer: 'May, June and September sit between the peak dry season and the heaviest rain, and give the best value of the year on both flights and rooms. December to February is the most expensive window, with Christmas to New Year the steepest of all. Songkran in mid-April pushes prices and crowds up sharply too.'
      },
      {
        question: 'How long is the flight from India to Thailand?',
        answer: 'Bangkok is one of the easiest short-haul trips from India. Kolkata is closest at roughly two and a half hours, while Delhi and Mumbai are around four and a half. Bengaluru and Chennai also fly direct. Fares move a lot with season and booking window, so check live prices for your dates rather than trusting any published figure.'
      },
      {
        question: 'What costs more than expected in Thailand?',
        answer: 'Island transfers, which can rival the price of a domestic flight; organised day tours, which carry a steep tourist premium over arranging the same trip yourself; and Bangkok airport taxis when the driver refuses the meter. Street food, trains and temple entry are cheap enough that they rarely trouble a budget.'
      },
      {
        question: 'Do Indians need a visa for Thailand?',
        answer: 'No, not for a tourist trip of up to 60 days. Indian passport holders enter under a visa exemption, which means there is nothing to apply for and no fee to pay at the airport. Only stays beyond 60 days require a tourist visa, applied for in advance at thaievisa.go.th.'
      }
    ]
  },
  'sri-lanka': {
    id: 'sri-lanka',
    name: 'Sri Lanka',
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80',
    bestTime: 'December to March',
    currency: 'Sri Lankan Rupee (LKR)',
    language: 'Sinhala, Tamil, English',
    climate: 'Tropical, 25-30°C year-round',
    tagline: 'Ancient temples, lush tea plantations, diverse wildlife, and scenic train journeys',
    overview: 'Sri Lanka, the enchanting "Pearl of the Indian Ocean," is a treasure trove of spiritual significance, natural wonders, and cultural richness packed into a relatively small island. This remarkable destination showcases stunning geographic and cultural diversity—from ancient Buddhist temples housing sacred relics to expansive tea plantations blanketing emerald hills, from pristine beaches to misty mountains. The iconic train journey through central highlands rivals the world\'s most scenic rail routes, winding through tea gardens and offering unforgettable vistas. Sri Lanka\'s diverse attractions include the magnificent Sigiriya rock fortress, charming colonial-era towns, whale-watching adventures, and some of Asia\'s best beaches. The island\'s multicultural heritage—with Buddhist, Hindu, Muslim, and Christian traditions coexisting peacefully—creates a unique spiritual atmosphere. Sri Lankan people are renowned for their genuine warmth and hospitality, welcoming visitors as honored guests. The country\'s exceptional value for money, combined with world-class experiences from hiking to diving to cultural immersion, makes it an ideal destination for every type of traveler.',
    visa: {
      summary: 'ETA (Electronic Travel Authorization) required for most nationalities including Indians.',
      details: [
        'ETA: 30 days single entry via www.eta.gov.lk',
        'ETA valid for 6 months, costs around $20-35 USD',
        'Process takes 24-48 hours',
        'Passport valid for 6 months minimum',
        'Alternatively, get 30-day visa on arrival'
      ],
      note: 'ETA is recommended - apply online before travel for faster processing'
    },
    packing: {
      'Clothing': ['Lightweight, breathable cotton clothes', 'Rain jacket or umbrella', 'Swim wear', 'Modest clothing for temples', 'Comfortable trekking shoes', 'Casual sandals'],
      'Accessories': ['High SPF sunscreen', 'Hat and sunglasses', 'Reusable water bottle', 'Insect repellent', 'Small day bag'],
      'Electronics': ['Phone charger', 'Universal adapter (Type D/M)', 'Portable power bank', 'Waterproof bag', 'Camera/GoPro']
    },
    attractions: [
      {
        name: 'Temple of the Sacred Tooth Relic',
        description: 'The holiest Buddhist temple in Sri Lanka, housing a sacred relic of Buddha in Kandy.',
        tip: 'Visit in evening for the golden lighting and smaller crowds'
      },
      {
        name: 'Sigiriya Lion Rock',
        description: 'Ancient fortress atop a 200m tall rock with panoramic views of surrounding plains.',
        tip: 'Start climb early morning to avoid afternoon heat'
      },
      {
        name: 'Tea Plantations of Nuwara Eliya',
        description: 'Lush green hills covered with tea gardens, scenic train rides, and tea factory tours.',
        tip: 'Stay overnight in hill country to witness stunning sunrise and sunset'
      },
      {
        name: 'Mirissa Beach & Whale Watching',
        description: 'Beautiful southern beach for relaxation and seasonal blue whale watching tours.',
        tip: 'Whale watching season is November to April'
      },
      {
        name: 'Yala National Park',
        description: 'Wildlife sanctuary with leopards, elephants, deer, and diverse bird species.',
        tip: 'Hire guides for safari jeep tours, best in early morning'
      }
    ],
    tips: [
      'Learn basic Sinhala phrases - locals appreciate the effort',
      'Cricket is a national passion - discuss matches if interested',
      'Respect Buddhist and Hindu traditions - remove shoes at temples',
      'Food is spicy - ask for "not spicy" if you prefer mild',
      'Street food is safe and delicious - try hoppers, kottu, and short eats',
      'Use tuk-tuks for short distances - agree on fare before boarding',
      'Rainfall is heavy - monsoon seasons vary by region',
      'Healthcare and pharmacies are good in main cities'
    ],
    faqs: [
      {
        question: 'How many days do you need in Sri Lanka?',
        answer: 'Eight to ten days covers the cultural triangle, the hill country and one stretch of coast without rushing. The island is compact but the roads are slow, so budget more travel time than the map distances suggest.'
      },
      {
        question: 'What is the best time to visit Sri Lanka?',
        answer: 'The island has two monsoons running on opposite schedules. December to March suits the west and south coasts and the cultural triangle, while May to September is the dry window for the east coast around Trincomalee and Arugam Bay. There is almost always somewhere in Sri Lanka with good weather.'
      },
      {
        question: 'Is the Kandy to Ella train worth it?',
        answer: 'It is one of the most scenic rail journeys anywhere, climbing through tea estates and cloud forest over roughly six to seven hours. Reserved seats sell out well in advance, so book early or be prepared to stand in the unreserved carriages, which many travellers actually prefer for the open doorways.'
      },
      {
        question: 'How do you get around Sri Lanka?',
        answer: 'Most visitors hire a car with a driver for the whole trip, which costs far less than it would in Europe and removes the strain of narrow mountain roads. Trains are best for the hill country stretch, and tuk-tuks handle short local hops.'
      },
      {
        question: 'Is Sri Lanka good for families?',
        answer: 'Yes, particularly for families with older children who can handle a few long drives. Wildlife safaris in Yala and Udawalawe, turtle hatcheries, calm southern beaches and short hikes give the trip variety, and food is mild enough to be familiar to Indian palates.'
      },
      {
        question: 'How much does a Sri Lanka trip cost from India?',
        answer: 'It is among the most affordable international trips available from India, largely because the flights are so short. The biggest variable is transport: hiring a car with a driver for the whole trip costs more than trains and buses but saves hours on slow mountain roads. Safari jeeps and park fees are the other real expense.'
      },
      {
        question: 'When is the cheapest time to visit Sri Lanka?',
        answer: 'April to June and September to November fall between the two monsoons and offer the best rates. Because the island has opposing wet seasons, you can usually find good weather somewhere even in the cheaper months, which is unusual for a destination this small.'
      },
      {
        question: 'How long is the flight from India to Sri Lanka?',
        answer: 'Colombo is barely over an hour from Chennai, roughly two and a half from Mumbai and under four from Delhi. It is the closest international destination for much of south India, which makes it viable even for a long weekend.'
      },
      {
        question: 'What costs more than expected in Sri Lanka?',
        answer: 'National park entry and safari jeep hire, which are priced in dollars and are the single largest line item for most visitors. Reserved train seats on the Kandy to Ella route also sell out far ahead, and last-minute private transport to make up for it is expensive.'
      }
    ]
  },
  'vietnam': {
    id: 'vietnam',
    name: 'Vietnam',
    image: 'https://images.unsplash.com/photo-1528127269322-539801943592?w=1200&q=80',
    bestTime: 'November to April',
    currency: 'Vietnamese Dong (VND)',
    language: 'Vietnamese, English in tourist areas',
    climate: 'Tropical and subtropical, 20-30°C',
    tagline: 'Rich history meets stunning landscapes, from Halong Bay to bustling cities',
    overview: 'Vietnam is a captivating destination where ancient history seamlessly intertwines with breathtaking natural beauty and vibrant urban energy. From the ethereal limestone karsts of Halong Bay rising majestically from emerald waters to the bustling streets of Hanoi and Ho Chi Minh City, Vietnam offers transformative travel experiences. The country\'s recent history adds profound depth, with museums and sites that honor both struggles and triumphs. Vietnam\'s people embody resilience and warmth, extending genuine hospitality to all visitors. The Mekong Delta reveals a unique way of life with floating villages and lush waterways, while ancient towns like Hoi An transport you back centuries with lantern-lit streets and traditional architecture. Vietnamese cuisine—featuring fresh herbs, bold flavors, and iconic dishes like pho and banh mi—ranks among the world\'s finest. Adventure awaits everywhere: cycling through rice paddies, trekking in mountain regions, exploring hidden caves, or simply getting lost in vibrant night markets. Vietnam combines affordability with extraordinary experiences, making it an essential Southeast Asian destination.',
    visa: {
      summary: 'Most nationals need a visa. E-Visa available online or get visa on arrival.',
      details: [
        'E-Visa: Single entry 90 days, valid 1 month (apply on www.evisa.gov.vn)',
        'Visa on Arrival: 30/60/90 day options via approval letter',
        'Passport valid 6 months minimum',
        'Processing time: 24-48 hours for E-Visa',
        'Cost: Approximately $15-25 USD'
      ],
      note: 'E-Visa is easiest option for Indians - apply 5-7 days before travel'
    },
    packing: {
      'Clothing': ['Light cotton clothing', 'Rain jacket', 'Comfortable walking shoes', 'Swim wear', 'Modest clothing', 'Light scarf'],
      'Accessories': ['Sunscreen', 'Hat and sunglasses', 'Water bottle', 'Day pack', 'Insect repellent'],
      'Electronics': ['Phone charger', 'Universal adapter (Type A/C)', 'Power bank', 'Camera', 'Waterproof bag']
    },
    attractions: [
      {
        name: 'Halong Bay',
        description: 'UNESCO World Heritage site with thousands of limestone karsts and emerald waters.',
        tip: 'Overnight cruise recommended for best experience'
      },
      {
        name: 'Old Quarter, Hanoi',
        description: 'Historic area with narrow streets, French colonial architecture, and vibrant street food.',
        tip: 'Wander streets in early morning for authentic local life'
      },
      {
        name: 'Hoi An Ancient Town',
        description: 'Well-preserved ancient town with lanterns, tailor shops, and riverside charm.',
        tip: 'Get a custom tailored suit - famous for quality and affordability'
      },
      {
        name: 'Mekong Delta',
        description: 'Rivers and canals with floating markets, rice paddies, and local life.',
        tip: 'Stay in a homestay to experience authentic Mekong culture'
      },
      {
        name: 'Cu Chi Tunnels',
        description: 'Historic war tunnels near Ho Chi Minh City offering insight into Vietnam War history.',
        tip: 'Guided tours available - very informative and moving experience'
      }
    ],
    tips: [
      'Vietnamese currency has many zeros - don\'t panic with numbers',
      'Street food is safe, delicious, and very affordable',
      'Traffic is intense - use pedestrian crossings carefully',
      'Motorbike rentals available but driving takes practice',
      'Tipping is not expected but small amounts appreciated',
      'Respect War history sites and be thoughtful when visiting',
      'Water tap is not drinkable - buy bottled water',
      'Haggle in markets but be respectful'
    ],
    faqs: [
      {
        question: 'How many days do you need in Vietnam?',
        answer: 'Twelve to fourteen days for the full north-to-south run through Hanoi, Halong Bay, Hoi An and Ho Chi Minh City. If you only have a week, pick one half of the country rather than trying to cover a nation that stretches over 1,600 km.'
      },
      {
        question: 'What is the best time to visit Vietnam?',
        answer: 'There is no single answer because the country spans several climate zones. November to April is broadly the driest window overall, but the north can be cold and misty in January while the south stays warm year-round. Central Vietnam sees its heaviest rain between September and November.'
      },
      {
        question: 'Should you travel Vietnam north to south or south to north?',
        answer: 'North to south is the more common route and usually the better one, since you finish in the warmer south and the internal flights back are frequent. The direction matters less than allowing a night in each place rather than treating them as stopovers.'
      },
      {
        question: 'Is Vietnam cheap to travel in?',
        answer: 'It is among the best value countries in Southeast Asia. Street food, local buses and family-run guesthouses cost very little; the spending tends to come from Halong Bay cruises, internal flights and tailored clothing in Hoi An.'
      },
      {
        question: 'Is it safe to ride a scooter in Vietnam?',
        answer: 'Traffic in Hanoi and Ho Chi Minh City is dense and follows an unwritten flow that takes time to read, so it is not the place to learn. Quieter routes around Hoi An, Ninh Binh and the Hai Van Pass are far more manageable. Check that your travel insurance covers riding and that you hold the licence it requires.'
      },
      {
        question: 'How much does a Vietnam trip cost from India?',
        answer: 'Day to day it is among the cheapest countries in the region, so the total is driven almost entirely by how much ground you cover. Internal flights between Hanoi, Da Nang and Ho Chi Minh City, a Halong Bay cruise and tailored clothing in Hoi An are where budgets actually go.'
      },
      {
        question: 'When is the cheapest time to visit Vietnam?',
        answer: 'May to September is low season for most of the country and prices drop accordingly, though central Vietnam sees heavy rain from September to November. Because the country spans several climate zones, cheaper months usually still work well for at least one region.'
      },
      {
        question: 'How long is the flight from India to Vietnam?',
        answer: 'Roughly five to six hours to Hanoi or Ho Chi Minh City from Delhi and Mumbai, with direct services on several routes. Connecting options through Bangkok, Kuala Lumpur or Singapore are common and sometimes cheaper, at the cost of several extra hours.'
      },
      {
        question: 'What costs more than expected in Vietnam?',
        answer: 'Halong Bay cruises, where the cheapest boats are a false economy and the good ones cost several times more; internal flights booked late; and the extras on tailoring in Hoi An, where the quoted price rarely includes the fabric you actually end up choosing.'
      }
    ]
  },
  'indonesia': {
    id: 'indonesia',
    name: 'Indonesia',
    image: 'https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=1200&q=80',
    bestTime: 'May to September',
    currency: 'Indonesian Rupiah (IDR)',
    language: 'Bahasa Indonesia, English in tourist areas',
    climate: 'Tropical, 23-31°C, humid',
    tagline: 'From Bali\'s spiritual temples to Komodo dragons, incredible island diversity',
    overview: 'Indonesia, the world\'s largest archipelago with over 17,000 islands, represents a spectacular convergence of biodiversity, cultural richness, and spiritual heritage. Bali dominates tourism with its terraced rice paddies, ornate temples, yoga retreats, and beaches, but Indonesia\'s true magic lies in exploring beyond the well-trodden path. Each island possesses distinct character and attractions—from Sumatra\'s rainforests and orangutan sanctuaries to Komodo\'s prehistoric dragons, from Java\'s volcanic peaks and ancient temples to Sulawesi\'s underwater wonders. Indonesia\'s Hindu culture on Bali contrasts beautifully with Muslim traditions elsewhere, creating theological diversity rarely found in one country. The Indonesian people exhibit remarkable warmth and hospitality across all islands. Traditional ceremonies, local crafts, and diverse cuisines showcase the archipelago\'s cultural complexity. Pristine beaches, world-class diving, jungle adventures, and mountain trekking offer infinite outdoor opportunities. Indonesia combines adventure, spirituality, culture, and natural beauty, providing transformative experiences at exceptional value. It\'s ideal for explorers seeking authentic encounters beyond typical tourist circuits.',
    visa: {
      summary: 'VOA (Visa on Arrival) available for many nationalities at Indonesian airports.',
      details: [
        'VOA: 30 days, extendable for another 30 days',
        'B211A visa valid for 60 days',
        'Apply online at www.molina.bnp2ri.go.id or get on arrival',
        'Need passport valid 6 months, return ticket',
        'Cost approximately $25-50 USD'
      ],
      note: 'Can also get approval letter online and get visa at airport'
    },
    packing: {
      'Clothing': ['Light, breathable clothing', 'Rain jacket', 'Swimwear', 'Sarong (for temples)', 'Comfortable shoes', 'Flip-flops'],
      'Accessories': ['High SPF sunscreen', 'Hat and sunglasses', 'Water bottle', 'Waterproof bag', 'Insect repellent'],
      'Electronics': ['Phone charger', 'Universal adapter (Type C/I)', 'Power bank', 'Camera', 'Snorkel gear optional']
    },
    attractions: [
      {
        name: 'Bali Temple Tours',
        description: 'Visit Tanah Lot (cliffside temple), Besakih (mother temple), and Ubud temples.',
        tip: 'Dress modestly - wear sarong provided at temples'
      },
      {
        name: 'Mount Bromo',
        description: 'Active volcano with stunning sunrise views and volcanic landscapes.',
        tip: 'Book jeep tours from night before, visit at 4am for sunrise'
      },
      {
        name: 'Komodo National Park',
        description: 'Home to Komodo dragons and pristine beaches with world-class snorkeling.',
        tip: 'Multi-day island-hopping tours recommended'
      },
      {
        name: 'Rice Terraces of Ubud',
        description: 'Iconic emerald-green rice paddies with trekking paths and traditional villages.',
        tip: 'Visit early morning and wear good walking shoes'
      },
      {
        name: 'Gili Islands',
        description: 'Car-free islands with beaches, snorkeling, and laid-back atmosphere.',
        tip: 'Perfect for relaxation and water sports'
      }
    ],
    tips: [
      'Rupiah currency comes in large denominations - expected',
      'Dress respectfully, especially in conservative areas',
      'Support local businesses - handcrafted souvenirs are excellent',
      'Food is delicious but can be spicy - learn "not spicy" phrase',
      'Stay hydrated - the humidity is intense',
      'Use reputable tour operators for activities',
      'Negotiate tuk-tuk fares before boarding',
      'Most places accept cards but carry cash for small transactions'
    ],
    faqs: [
      {
        question: 'How many days do you need in Bali?',
        answer: 'Seven to eight days lets you split time between the south coast, Ubud and one outer island such as Nusa Penida or the Gilis. Five days is workable if you stay in two bases. Traffic between Bali\'s regions is slow, so fewer moves means more actual holiday.'
      },
      {
        question: 'What is the best time to visit Indonesia?',
        answer: 'May to September is the dry season across Bali, Java and Komodo, with July and August the busiest and priciest. The shoulder months of May, June and September usually give the same weather with noticeably thinner crowds.'
      },
      {
        question: 'Is there more to Indonesia than Bali?',
        answer: 'Considerably more. Java has Borobudur and the volcanic sunrise at Bromo and Ijen, Lombok offers quieter beaches and Rinjani, Komodo has the dragons and pink sand, and Sumatra has orangutans. Bali is the easy entry point, not the whole country.'
      },
      {
        question: 'How do you get around Bali?',
        answer: 'Hiring a car with a driver by the day is the standard approach and costs less than most visitors expect. Scooters are cheap and common but the roads are chaotic and police checks on licences are routine. Ride-hailing apps work in the south but are restricted in some areas by local taxi associations.'
      },
      {
        question: 'Is Bali suitable for vegetarians?',
        answer: 'Very much so. Ubud in particular has a dense concentration of vegetarian and vegan restaurants, and Indonesian staples like gado-gado, tempeh and nasi goreng adapt easily. Ask for dishes without terasi, the shrimp paste that appears in many sambals.'
      },
      {
        question: 'How much does a Bali trip cost from India?',
        answer: 'In the May, June and September shoulder months, budget roughly \u20b965,000 to \u20b980,000 per person for seven nights on a modest budget, and \u20b995,000 to \u20b91,40,000 for mid-range stays and private drivers. Return flights from Delhi or Mumbai are usually the largest single line item. July, August and December cost noticeably more for the same trip.'
      },
      {
        question: 'What are the fixed entry costs for Bali?',
        answer: 'Two charges are fixed and unavoidable. Visa on arrival is IDR 500,000, around \u20b92,600, and the Bali tourist levy is IDR 150,000, around \u20b9800. The levy is paid once per trip, not per night. Together they add roughly \u20b93,400 per person before you have booked anything else.'
      },
      {
        question: 'What is the Bali tourist levy and how do you pay it?',
        answer: 'It is a compulsory IDR 150,000 charge on every foreign visitor, introduced by the Bali provincial government to fund conservation and cultural upkeep. Pay it online in advance at the official portal lovebali.baliprov.go.id, or at a counter on arrival. You receive a QR voucher that officials can scan, so keep it on your phone.'
      },
      {
        question: 'Which is the cheapest month to visit Bali?',
        answer: 'The shoulder months of May, June and September give dry-season weather at low-season prices, and are the best value overall. February and March are cheaper still but sit in the wet season. Avoid July, August and the Christmas to New Year window, when flights and villas can cost nearly double.'
      },
      {
        question: 'What is a good seven day Bali itinerary?',
        answer: 'Two nights in the south around Seminyak or Canggu, three nights in Ubud for the rice terraces, temples and waterfalls, then two nights on Nusa Penida or the Gili islands. Keeping to three bases avoids losing half your days to Bali\'s slow traffic, which is the most common first-timer mistake.'
      },
      {
        question: 'How much should you budget per day in Bali?',
        answer: 'Excluding flights, plan on roughly \u20b92,500 to \u20b94,000 a day for a guesthouse, local food and a scooter, or \u20b96,000 to \u20b912,000 a day for a mid-range villa, restaurant meals and a car with driver. Entrance fees to temples and waterfalls are small, usually under \u20b9300 each.'
      },
      {
        question: 'Is Bali or the Maldives better value from India?',
        answer: 'Bali costs less for the same number of nights because you are not paying resort-island transfer and monopoly pricing, and it offers far more to do beyond the beach. The Maldives wins on water clarity and privacy. For a first trip with a limited budget, Bali stretches further.'
      }
    ]
  },
  'malaysia': {
    id: 'malaysia',
    name: 'Malaysia',
    image: 'https://images.unsplash.com/photo-1506012787146-f92b2d7d6d96?w=1200&q=80',
    bestTime: 'December to March',
    currency: 'Malaysian Ringgit (MYR)',
    language: 'Malay, English widely spoken',
    climate: 'Tropical, 25-35°C, humid',
    tagline: 'Cultural diversity, modern cities, tropical rainforests, and pristine beaches',
    overview: 'Malaysia exemplifies seamless integration of modernity, natural preservation, and multicultural harmony. This Southeast Asian success story balances rapid development with environmental conservation, offering visitors contemporary cities alongside pristine wilderness. Kuala Lumpur showcases futuristic architecture like the iconic Petronas Twin Towers, excellent infrastructure, and diverse neighborhoods reflecting its Chinese, Indian, and Malay populations. Malaysia\'s geographic diversity ranges from tropical rainforests (among the world\'s oldest) to stunning islands and beaches offering excellent diving and snorkeling. The Cameron Highlands and tea plantations provide cool mountain retreats with misty vistas, while George Town offers colonial charm and street art. Malaysia excels at multicultural coexistence, with mosques, temples, and churches standing harmoniously alongside each other. The food scene is legendary—each culinary tradition maintains authenticity while blending together. Malaysian people are welcoming, English is widely spoken, and public transport is excellent and affordable. The country combines adventure opportunities (rainforest trekking, island hopping, caving) with comfort and accessibility, making it ideal for both adventurous travelers and those seeking relaxation with modern amenities.',
    visa: {
      summary: 'Indian passport holders enter visa-free for up to 30 days under a temporary exemption that runs until 31 December 2026.',
      details: [
        'The exemption covers tourism, business meetings, social visits and transit, but not employment',
        'Stays are limited to 30 days, not 90',
        'Complete the Malaysia Digital Arrival Card (MDAC) online within three days before arrival',
        'Passport valid at least six months beyond your entry date',
        'Return or onward ticket and proof of accommodation may be checked',
        'India otherwise sits on Malaysia\'s visa-required list, so an eVISA or eNTRI is needed for longer stays'
      ],
      note: 'This is a temporary waiver with an expiry date. Confirm it is still in force at imi.gov.my before booking travel beyond 2026.'
    },
    packing: {
      'Clothing': ['Lightweight clothes', 'Rain jacket', 'Swimwear', 'Modest clothing', 'Comfortable shoes', 'Sandals'],
      'Accessories': ['Sunscreen', 'Hat and sunglasses', 'Water bottle', 'Insect repellent', 'Day pack'],
      'Electronics': ['Phone charger', 'Universal adapter (Type G)', 'Power bank', 'Camera']
    },
    attractions: [
      {
        name: 'Petronas Twin Towers',
        description: 'Iconic 452m skyscrapers with skybridge, observation deck, and shopping mall.',
        tip: 'Visit in late afternoon and stay for sunset'
      },
      {
        name: 'Kuala Lumpur City Center',
        description: 'Modern city with parks, museums, mosques, and vibrant street food scene.',
        tip: 'Explore Chinatown and Merdeka Square areas'
      },
      {
        name: 'Cameron Highlands Tea Plantations',
        description: 'Hill station with tea gardens, cool climate, and strawberry farms.',
        tip: '3-4 hour drive from KL, worth overnight stay'
      },
      {
        name: 'Taman Negara National Park',
        description: 'One of world\'s oldest rainforests with canopy walks and wildlife.',
        tip: 'Hire guide for jungle trekking - sightings guaranteed'
      },
      {
        name: 'Penang Island',
        description: 'Historic Georgetown (UNESCO site), beaches, and multicultural cuisine.',
        tip: 'Georgetown street art scene and food trails are must-see'
      }
    ],
    tips: [
      'Malaysia is visa-free for Indians for 90 days - huge advantage',
      'Food is multicultural - Malay, Chinese, and Indian cuisines mixed',
      'Respect Islamic customs - Friday prayers time avoid non-Muslim areas',
      'Public transportation is excellent via LRT, MRT, buses',
      'Petronas Twin Towers and KL Tower offer stunning city views',
      'Shopping malls are air-conditioned havens from heat',
      'Tipping is not mandatory but appreciated',
      'Safety is good - one of the safest countries in region'
    ],
    faqs: [
      {
        question: 'How many days do you need in Malaysia?',
        answer: 'Seven to ten days covers Kuala Lumpur, Penang and one beach destination such as Langkawi. Four days is enough for Kuala Lumpur alone with a day trip to the Batu Caves or Malacca.'
      },
      {
        question: 'What is the best time to visit Malaysia?',
        answer: 'December to March suits the west coast including Langkawi, Penang and Kuala Lumpur. The east coast islands like Perhentian and Redang run on the opposite monsoon and are best from March to October, with many resorts closing entirely over the winter months.'
      },
      {
        question: 'Penang or Langkawi - which is better?',
        answer: 'Penang is the choice for food, street art and colonial architecture in George Town, with beaches as a secondary attraction. Langkawi is the choice for beaches, island hopping and duty-free shopping. Many itineraries fit both, since they are a short flight or ferry apart.'
      },
      {
        question: 'Is Malaysia good for vegetarian travellers?',
        answer: 'Excellent. The large Indian community means banana leaf restaurants and vegetarian thalis are easy to find in Kuala Lumpur, Penang and Ipoh, and Chinese Buddhist vegetarian eateries are common. Malay dishes often contain shrimp paste, so ask before ordering.'
      },
      {
        question: 'Is Malaysia a good family destination?',
        answer: 'Yes. Distances are short, roads are good, English is widely spoken, and the mix of city attractions, rainforest and beaches gives children variety. Legoland in Johor and the Langkawi cable car are reliable favourites.'
      },
      {
        question: 'Do Indians need a visa for Malaysia?',
        answer: 'Not at present. Indian passport holders enter visa-free for up to 30 days under a temporary exemption running until 31 December 2026. You must still complete the Malaysia Digital Arrival Card online within three days before arrival. The waiver has an expiry date, so confirm it at imi.gov.my before booking.'
      },
      {
        question: 'How much does a Malaysia trip cost from India?',
        answer: 'Malaysia sits between Thailand and Singapore on price and is one of the better value trips in the region. Kuala Lumpur and Penang are inexpensive day to day; Langkawi and the east coast islands cost more, and the internal flights or ferries to reach them are the main addition.'
      },
      {
        question: 'When is the cheapest time to visit Malaysia?',
        answer: 'The wet months on whichever coast you are visiting bring the lowest rates. For Langkawi and Penang that means roughly September to November, while the east coast islands are cheapest, or closed entirely, from November to February. Kuala Lumpur stays broadly consistent year-round.'
      },
      {
        question: 'How long is the flight from India to Malaysia?',
        answer: 'Around four and a half hours to Kuala Lumpur from Chennai and roughly five to five and a half from Delhi and Mumbai, with direct flights from several Indian cities. Kuala Lumpur is also a common connecting hub, which keeps fares competitive.'
      },
      {
        question: 'What costs more than expected in Malaysia?',
        answer: 'Domestic flights and ferries to the islands, which can cost as much as a night of accommodation; Langkawi activities, which are priced for tourists rather than locals; and taxis in Kuala Lumpur when arranged outside a ride-hailing app.'
      }
    ]
  },
  'singapore': {
    id: 'singapore',
    name: 'Singapore',
    image: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=1200&q=80',
    bestTime: 'February to April, August to September',
    currency: 'Singapore Dollar (SGD)',
    language: 'English, Mandarin, Malay, Tamil',
    climate: 'Tropical, 24-32°C, humid year-round',
    tagline: 'Futuristic architecture meets diverse cultures and incredible street food',
    overview: 'Singapore represents urban development perfection—a gleaming city-state where cutting-edge technology, impeccable organization, and multicultural vibrancy coexist harmoniously. Despite being one of the world\'s most developed nations, Singapore maintains authentic cultural neighborhoods, each distinct and proud of its heritage. Chinatown bustles with traditional shophouses and ancient temples, Little India vibrates with colors and spices, Arab Street preserves Islamic heritage and vintage charm. Marina Bay Sands symbolizes architectural innovation, while Gardens by the Bay displays nature-integrated modern design. Hawker centers—where locals eat—serve world-class cuisine at remarkable prices, rivaling fine dining establishments. Singapore\'s public transportation is exemplary: clean, efficient, punctual, and affordable. The city celebrates diversity through festivals honoring Chinese, Hindu, Islamic, and Christian traditions simultaneously. Despite being expensive compared to Southeast Asian neighbors, Singapore offers exceptional value through quality, safety, efficiency, and experiences. The city provides an excellent base for exploring Southeast Asia while offering refined entertainment, shopping, and dining. Whether you\'re seeking cultural experiences, gastronomic adventures, architectural wonders, or green spaces, Singapore delivers excellence across all domains.',
    visa: {
      summary: 'Indian passport holders need a Singapore visa arranged before travel. It cannot be obtained on arrival.',
      details: [
        'Apply online through a local contact or strategic partner in Singapore, or through a Singapore Overseas Mission or its authorised visa agent',
        'S$30 non-refundable processing fee',
        'Processed in about three working days, so apply well before you fly',
        'Submit within 30 days of your intended arrival date',
        'Passport valid at least six months beyond your entry date',
        'The SG Arrival Card is not a visa - it is a separate declaration filed before travel',
        'Indian nationals may qualify for the Visa-Free Transit Facility when transiting to a third country'
      ],
      note: 'A visa permits you to seek entry; the length of stay is set by the officer at the checkpoint. Check ica.gov.sg before booking.'
    },
    packing: {
      'Clothing': ['Light, breathable clothes', 'Light cardigan for AC', 'Swimwear', 'Comfortable shoes', 'Smart casual for nice restaurants'],
      'Accessories': ['Sunscreen SPF 50+', 'Hat and sunglasses', 'Reusable water bottle', 'Day pack'],
      'Electronics': ['Phone charger', 'Universal adapter (Type G)', 'Power bank', 'Camera']
    },
    attractions: [
      {
        name: 'Gardens by the Bay',
        description: 'Futuristic 54-hectare park with Supertrees, domes, and nightly light show.',
        tip: 'Visit at night for magical Supertree light show'
      },
      {
        name: 'Marina Bay Sands',
        description: 'Iconic hotel with observation deck (57th floor) offering 360° city views.',
        tip: 'Rooftop infinity pool is for guests only, but observation deck is public'
      },
      {
        name: 'Chinatown and Hindu Temple',
        description: 'Historic areas with street food, temples, and cultural experiences.',
        tip: 'Chinatown weekend night market is vibrant and fun'
      },
      {
        name: 'Sentosa Island',
        description: 'Island resort with beaches, museums, adventure parks, and Universal Studios.',
        tip: 'Multiple attractions - plan full day visit'
      },
      {
        name: 'Orchard Road',
        description: 'Premier shopping district with malls, restaurants, and entertainment.',
        tip: 'Best for shopping, browsing, and window shopping'
      }
    ],
    tips: [
      'Singapore is expensive compared to other SE Asia countries',
      'Public transportation is world-class - use EZ-Link card',
      'Hawker centers have incredible food at affordable prices',
      'Chewing gum is banned - don\'t import or dispose carelessly',
      'Littering and vandalism have heavy fines - respect cleanliness',
      'No visa required for 90 days - very convenient',
      'English widely spoken - easy to navigate',
      'Weather is hot and humid - stay hydrated always'
    ],
    faqs: [
      {
        question: 'How many days do you need in Singapore?',
        answer: 'Three to four days covers Marina Bay, Gardens by the Bay, Sentosa, the heritage districts and a night safari. The city is compact and the metro is fast, so you can see a great deal in a short stay. Many travellers pair it with Malaysia or Bali rather than visiting alone.'
      },
      {
        question: 'What is the best time to visit Singapore?',
        answer: 'Singapore sits near the equator and stays hot and humid all year, roughly 25-32°C with afternoon showers common. February to April is marginally drier. Rain rarely disrupts a trip because so many attractions are indoors or covered.'
      },
      {
        question: 'Is Singapore expensive?',
        answer: 'Accommodation and alcohol are genuinely expensive, but food need not be. Hawker centres serve excellent meals for a fraction of restaurant prices, and several have Michelin recognition. The metro is cheap and many of the best sights, including the Gardens by the Bay outdoor areas, are free.'
      },
      {
        question: 'Is Singapore worth a stopover?',
        answer: 'Yes, and Changi itself is an attraction with the Jewel waterfall and gardens. A layover of eight hours or more is enough to reach Marina Bay and return comfortably. Free city tours are offered to eligible transit passengers.'
      },
      {
        question: 'What are the fines for tourists in Singapore?',
        answer: 'Singapore enforces fines for littering, jaywalking, eating or drinking on the MRT, and smoking outside designated areas. Chewing gum cannot be imported for sale. Drug offences carry extremely severe penalties. An ordinary visitor who follows the posted signs will never encounter any of this.'
      },
      {
        question: 'Do Indians need a visa for Singapore?',
        answer: 'Yes. Indian passport holders must obtain a Singapore visa before travelling; it is not available on arrival. Apply through a local contact in Singapore or an authorised visa agent, pay the S$30 processing fee and allow about three working days. Only diplomatic, official and service passport holders are exempt.'
      },
      {
        question: 'How much does a Singapore trip cost from India?',
        answer: 'Singapore is the most expensive stop in Southeast Asia, and accommodation is the reason. Food need not be: hawker centres cost a fraction of restaurants. Because three or four days is enough, the shorter stay offsets the higher daily rate compared with a longer trip elsewhere.'
      },
      {
        question: 'When is the cheapest time to visit Singapore?',
        answer: 'Sitting near the equator, Singapore has no real off season for weather, so rates track events rather than climate. Avoid the Formula One weekend, major conference periods and Chinese New Year, when hotel prices rise sharply. February and the quieter mid-year weeks are usually the best value.'
      },
      {
        question: 'How long is the flight from India to Singapore?',
        answer: 'About four and a half hours from Chennai and roughly five and a half from Delhi and Mumbai, with frequent direct flights from most major Indian cities. Changi is also one of the busiest connecting hubs in Asia, so onward options are plentiful.'
      },
      {
        question: 'What costs more than expected in Singapore?',
        answer: 'Alcohol, which carries steep duty and can double a dinner bill; hotel rooms, which are small and dear by regional standards; and Sentosa, where individual attractions are ticketed separately and a family day adds up faster than expected.'
      }
    ]
  },
  'cambodia': {
    id: 'cambodia',
    name: 'Cambodia',
    image: 'https://images.unsplash.com/photo-1528181304800-259b08848526?w=1200&q=80',
    bestTime: 'November to March',
    currency: 'Cambodian Riel (KHR)',
    language: 'Khmer, English in tourist areas',
    climate: 'Tropical, 25-33°C',
    tagline: 'Home to the magnificent Angkor Wat, ancient temples, and a rich cultural heritage',
    overview: 'Cambodia, home to Southeast Asia\'s most iconic monument Angkor Wat, is a nation profoundly shaped by ancient glory and recent resilience. The Angkor Archaeological Park represents human achievement of extraordinary magnitude—a complex of over 900 temples spanning centuries of Khmer civilization. Witnessing Angkor Wat at sunrise is a transformative experience that transcends typical tourism. Beyond temples, Cambodia offers cultural immersion through floating villages on Tonle Sap Lake, bustling night markets in Siem Reap, and the capital Phnom Penh\'s museums revealing complex history. Cambodia\'s recent past, including tragic periods, has created a profoundly spiritual and resilient population. Despite historical challenges, Cambodian people exemplify warmth, grace, and forward-looking optimism. The country remains refreshingly untouched by mass tourism compared to neighboring Thailand and Vietnam, preserving authenticity. Cambodian cuisine—less spicy than Thai but equally flavorful—deserves exploration. The country offers remarkable value, with exceptional experiences available at minimal cost. Visiting Cambodia supports a developing nation working toward prosperity while allowing travelers to witness genuine history, spirituality, and human resilience. It\'s an essential Southeast Asian destination for culturally conscious travelers.',
    visa: {
      summary: 'E-Visa required for most nationalities. Can apply online or get on arrival.',
      details: [
        'E-Visa: Tourist (T) or Business (B) via www.evisa.gov.kh',
        '30-day tourist visa costs around $25 USD',
        'Processing takes 24-48 hours',
        'Can also get visa on arrival at airports',
        'Passport valid 6 months beyond stay'
      ],
      note: 'E-Visa is recommended - online process is simple and fast'
    },
    packing: {
      'Clothing': ['Light cotton clothing', 'Rain jacket', 'Swimwear', 'Modest clothes for temples', 'Comfortable trekking shoes', 'Sandals'],
      'Accessories': ['High SPF sunscreen', 'Hat and sunglasses', 'Water bottle', 'Insect repellent', 'Lightweight scarf'],
      'Electronics': ['Phone charger', 'Universal adapter (Type A/C)', 'Power bank', 'Camera', 'Headlamp']
    },
    attractions: [
      {
        name: 'Angkor Wat',
        description: 'World\'s largest religious monument, stunning architecture and historical significance.',
        tip: 'Hire guide, start early morning, 3-4 days minimum recommended'
      },
      {
        name: 'Phnom Penh Royal Palace',
        description: 'Official residence of Cambodian Monarch with Silver Pagoda and valuable artifacts.',
        tip: 'Dress modestly - covered shoulders and knees required'
      },
      {
        name: 'Tonle Sap Lake',
        description: 'Massive freshwater lake with floating villages and seasonal changes.',
        tip: 'Sunrise boat tours show fishing communities and abundance of life'
      },
      {
        name: 'Sihanoukville Beach',
        description: 'Coastal town with beaches, islands, and water sports.',
        tip: 'Nearby islands great for snorkeling and diving'
      },
      {
        name: 'Kampong Phluk',
        description: 'Flooded mangrove forests accessible by kayak or canoe.',
        tip: 'Visit during wet season (July-January) when water is higher'
      }
    ],
    tips: [
      'Cambodian culture is deeply Buddhist - show respect at temples',
      'Street food is excellent - try local specialties like nom banh chok',
      'Landmine awareness is important - stay on marked paths',
      'Tuk-tuks are main transport - negotiate price beforehand',
      'Riel currency has many zeros - USD widely accepted too',
      'Tipping is appreciated but not mandatory',
      'Travel respectfully - remember history is still fresh',
      'Bring insect repellent - mosquitoes common'
    ],
    faqs: [
      {
        question: 'How many days do you need in Cambodia?',
        answer: 'Four to five days in Siem Reap covers the main Angkor temples and the floating villages at a reasonable pace. Seven to eight days lets you add Phnom Penh and the history at Tuol Sleng, or the southern coast around Kampot.'
      },
      {
        question: 'How many days do you need for Angkor Wat?',
        answer: 'Three days is the sweet spot. One day covers the headline temples of Angkor Wat, Bayon and Ta Prohm, and the extra days let you reach Banteay Srei and Beng Mealea without temple fatigue. Passes are sold as one, three or seven day tickets.'
      },
      {
        question: 'What is the best time to visit Cambodia?',
        answer: 'November to March is the cool dry season and by far the most comfortable for temple days. April and May are punishingly hot, often above 38°C. The rainy months from June to October bring green landscapes, full moats and far fewer visitors.'
      },
      {
        question: 'What currency should you carry in Cambodia?',
        answer: 'US dollars are used for most transactions and ATMs commonly dispense them. Cambodian riel appears mainly as small change, usually for amounts under a dollar. Carry small denomination notes, as change for large bills can be difficult.'
      },
      {
        question: 'Is Angkor Wat sunrise worth waking up for?',
        answer: 'It is genuinely spectacular but very crowded, with hundreds gathered at the reflecting pools before dawn. Arriving around 5am secures a reasonable spot. Sunset at Pre Rup or a quiet mid-morning at a lesser temple can be just as rewarding with a fraction of the crowd.'
      },
      {
        question: 'How much does a Cambodia trip cost from India?',
        answer: 'Food and accommodation are cheap, so the budget is dominated by two fixed costs: the Angkor pass and getting there, since no direct flights operate from India. Tuk-tuk drivers hired by the day for temple runs are the other recurring expense, and worth every dollar.'
      },
      {
        question: 'When is the cheapest time to visit Cambodia?',
        answer: 'May to October, the green season. Rain usually falls in short afternoon bursts rather than all day, the moats and countryside look their best, and both rates and crowds drop sharply. April is cheap for a reason, with temperatures often above 38\u00b0C on exposed temple sites.'
      },
      {
        question: 'How long is the flight from India to Cambodia?',
        answer: 'There are no direct flights, so plan on eight to eleven hours in total connecting through Bangkok, Kuala Lumpur, Singapore or Ho Chi Minh City. Many travellers reach Siem Reap overland from Bangkok instead, which is slower but pairs the two countries naturally.'
      },
      {
        question: 'What costs more than expected in Cambodia?',
        answer: 'The Angkor pass, sold as one, three or seven day tickets and priced in dollars; guided temple tours, which are worth it but not cheap; and the habit of quoting everything in US dollars, which makes small purchases feel deceptively minor until they accumulate.'
      }
    ]
  },
  'laos': {
    id: 'laos',
    name: 'Laos',
    image: 'https://images.unsplash.com/photo-1540611025311-01df3cef54b5?w=1200&q=80',
    bestTime: 'October to March',
    currency: 'Lao Kip (LAK)',
    language: 'Lao, English in tourist areas',
    climate: 'Tropical, 20-30°C, monsoon seasons',
    tagline: 'Peaceful temples, the mighty Mekong River, and a wonderfully laid-back atmosphere',
    overview: 'Laos represents the road less traveled in Southeast Asia—a landlocked country that feels genuinely untouched by mass commercialization. This spiritual nation embodies Buddhist principles through daily life, evident in the gentle demeanor of its people and peaceful atmosphere pervading even urban areas. Luang Prabang, a UNESCO World Heritage site, transports visitors to a slower era with French colonial architecture, ornate temples, and the serene Mekong River flowing through town. Watching thousands of saffron-robed monks collect alms at dawn creates profound spiritual moments. Multi-day Mekong River cruises offer meditative journeys past fishing villages, stopping in remote communities where locals maintain traditional ways of life. Laos teaches the value of slowing down—the country operates on "Laos time" where rushing is abandoned in favor of appreciating the present moment. Laos\'s least-explored temples, caves, and natural areas provide adventure for those venturing beyond Luang Prabang. Sticky rice and flavorful curries showcase local cuisine. The country\'s tourism infrastructure remains minimal, preserving authenticity and genuine interactions with locals. Laos offers transformative experiences emphasizing mindfulness, spirituality, and connection to nature. Visiting Laos before rapid development arrives provides access to Southeast Asia\'s last relatively unexploited destinations.',
    visa: {
      summary: 'Visa on Arrival available at Lao airports and border crossings.',
      details: [
        '30-day tourist visa on arrival costs $40-50 USD',
        'E-Visa available at www.laoevisa.gov.la ($45)',
        'Need passport valid 6 months, photos, return ticket',
        'Processing at airport takes 15-30 minutes',
        'VOA validity is from issue date'
      ],
      note: 'Visa on Arrival is straightforward process'
    },
    packing: {
      'Clothing': ['Light breathing clothes', 'Rain jacket', 'Swimwear', 'Modest temple clothes', 'Comfortable shoes', 'Sandals'],
      'Accessories': ['Sunscreen', 'Hat and sunglasses', 'Water bottle', 'Day pack', 'Insect repellent'],
      'Electronics': ['Phone charger', 'Universal adapter (Type A/C/I)', 'Power bank', 'Camera', 'Torch/headlamp']
    },
    attractions: [
      {
        name: 'Luang Prabang',
        description: 'Ancient royal capital with temples, French colonial architecture, UNESCO site.',
        tip: 'Alms giving ceremony at dawn is spiritual experience'
      },
      {
        name: 'Vang Vieng',
        description: 'Scenic limestone mountains, tubing down Nam Song river, cave exploration.',
        tip: 'Cave tours and rock climbing available'
      },
      {
        name: 'Mekong River',
        description: 'Mighty river with scenic cruises, sunset boats, and village visits.',
        tip: 'Sunset cruise from Vientiane or Luang Prabang is romantic'
      },
      {
        name: '4000 Islands (Si Phan Don)',
        description: 'Island cluster in south with waterfalls, dolphins, and peaceful atmosphere.',
        tip: 'Stay on island, kayak through channels, perfect for relaxation'
      },
      {
        name: 'Kuang Si Waterfalls',
        description: 'Turquoise multi-tiered waterfall with swimming pools and jungle trekking.',
        tip: 'Visit early morning before crowds arrive'
      }
    ],
    tips: [
      'Laos is peaceful and welcoming - very safe for travelers',
      'Respect Buddhist culture - visit temples with reverence',
      'Food is delicious - sticky rice is staple, try fresh spring rolls',
      'Tourism infrastructure varies - book ahead in peak season',
      'USD widely accepted alongside Kip currency',
      'Tipping not mandatory but appreciated',
      'River transport common - ferries are inexpensive',
      'Electricity and infrastructure less developed than neighbors'
    ],
    faqs: [
      {
        question: 'How many days do you need in Laos?',
        answer: 'Seven to ten days covers Luang Prabang, Vang Vieng and Vientiane at the unhurried pace the country invites. Luang Prabang alone deserves three days, and rushing it defeats the point of visiting the most relaxed country in the region.'
      },
      {
        question: 'What is the best time to visit Laos?',
        answer: 'October to March is the cool dry season and the most pleasant window. March and April bring heavy agricultural burning that leaves a persistent haze across the north and can obscure the views entirely. The wet season from May to September makes waterfalls spectacular.'
      },
      {
        question: 'Is the slow boat down the Mekong worth it?',
        answer: 'The two-day slow boat from Huay Xai to Luang Prabang is a classic overland journey with an overnight stop in Pakbeng. It is scenic and sociable but the wooden seats are hard, so bring a cushion. Those short on time fly instead, which takes about an hour.'
      },
      {
        question: 'Is Laos expensive?',
        answer: 'It is one of the most affordable countries in Southeast Asia for food, guesthouses and local transport. The costs that add up are organised activities such as the Kuang Si tours, kayaking and zip-lining, plus the high-speed railway tickets that now connect the main cities.'
      },
      {
        question: 'What is the alms giving ceremony and can visitors watch?',
        answer: 'Each dawn in Luang Prabang, monks walk the streets collecting offerings from residents. Visitors may observe respectfully from a distance, standing back, staying silent and not using flash. Do not participate unless you understand the ritual, and never block the procession for photographs.'
      },
      {
        question: 'How much does a Laos trip cost from India?',
        answer: 'Laos is one of the cheapest countries in the region for food, guesthouses and local transport. The budget goes on getting there, since no direct flights operate from India, and on organised activities such as the Kuang Si tours, kayaking and zip-lining that make up most itineraries.'
      },
      {
        question: 'When is the cheapest time to visit Laos?',
        answer: 'May to September, the wet season, when rates fall and the waterfalls are at their most dramatic. March and April are also cheap but carry heavy agricultural burning haze across the north, which can obscure views entirely and irritate the lungs.'
      },
      {
        question: 'How long is the flight from India to Laos?',
        answer: 'No direct flights operate, so expect nine to twelve hours in total connecting through Bangkok, Hanoi or Kuala Lumpur. Many travellers arrive overland or by river from northern Thailand instead, which suits the pace of the country better than rushing in by air.'
      },
      {
        question: 'What costs more than expected in Laos?',
        answer: 'Organised adventure activities, which are the main draw and priced for foreign visitors; the high-speed railway, which is excellent but sells out and pushes people onto pricier alternatives; and the two-day slow boat, once you add the compulsory overnight stop in Pakbeng.'
      }
    ]
  },
  'philippines': {
    id: 'philippines',
    name: 'Philippines',
    image: 'https://images.unsplash.com/photo-1518509562904-e7ef99cdcc86?w=1200&q=80',
    bestTime: 'December to May',
    currency: 'Philippine Peso (PHP)',
    language: 'Filipino/Tagalog, English widely spoken',
    climate: 'Tropical, 25-32°C, typhoon season June-November',
    tagline: 'Pristine beaches, world-class diving spots, and the warmest hospitality in Southeast Asia',
    overview: 'The Philippines, an archipelago of over 7,000 islands, captivates visitors with pristine beaches, exceptional diving, and legendarily warm Filipino hospitality. Each island region possesses distinct character—Boracay offers beach paradise, Palawan presents limestone wonders and eco-adventures, Cebu blends modern development with beach culture, and Bohol enchants with unique landscapes. World-class diving sites, including WWII wreck explorations, rank among global diving destinations. Island hopping represents the quintessential Philippines experience, revealing hidden beaches, secret lagoons, and local communities. Filipino people embody the concept of bayanihan (community unity), creating genuine connections with visitors and treating them as extended family members. The culture uniquely blends Asian, Spanish, and American influences creating something entirely original. Philippine cuisine combines fresh seafood, tropical fruits, and unique dishes reflecting cultural heritage. The country offers adventure, relaxation, culture, and affordability. Philippines develops rapidly while maintaining island charm and authentic interactions. English is widely spoken, facilitating communication. Despite typhoon seasons requiring careful planning, the Philippines delivers transformative island-hopping experiences, genuine human connections, and memories of tropical paradise.',
    visa: {
      summary: 'Indian passport holders get 14 days visa-free, extended to 30 days if you hold a valid visa or residence permit from Australia, Japan, the USA, Canada, the Schengen Area, Singapore or the UK.',
      details: [
        'The standard 14-day entry is for tourism only and applies to all Indian passport holders',
        'The 30-day entry requires the qualifying visa or permit to be valid and visible in your passport',
        'Neither stay can be extended or converted once you are in the country',
        'Register with eTravel within 72 hours before departure',
        'Passport valid at least six months, plus a return or onward ticket',
        'Proof of sufficient funds may be requested at immigration',
        'For longer trips apply for an e-Visa or a 9A visitor visa before you travel'
      ],
      note: 'Plan the trip around 14 days unless you hold one of the qualifying visas. Overstaying carries fines.'
    },
    packing: {
      'Clothing': ['Light, breathable clothes', 'Rain jacket', 'Swimwear', 'Rash guard for diving', 'Comfortable shoes', 'Sandals'],
      'Accessories': ['High SPF sunscreen', 'Hat and sunglasses', 'Water bottle', 'Insect repellent', 'Reef-safe sunscreen'],
      'Electronics': ['Phone charger', 'Universal adapter (Type A/B)', 'Power bank', 'Camera', 'Waterproof case']
    },
    attractions: [
      {
        name: 'Palawan Island',
        description: 'El Nido and Puerto Princesa - world-class beaches, island hopping, and limestone cliffs.',
        tip: 'Island hopping tours at El Nido - Big and Small Lagoons must-see'
      },
      {
        name: 'Boracay Island',
        description: 'Famous white-sand beach, water sports, nightlife, and beach resorts.',
        tip: 'Best for sunset on White Beach - magical experience'
      },
      {
        name: 'Siargao Island',
        description: 'Surfing paradise, cloud nine beach, and laid-back island vibes.',
        tip: 'March-May is dry season and best for surfing'
      },
      {
        name: 'Intramuros and Manila',
        description: 'Historic walled city with museums, churches, and cultural sites.',
        tip: 'Fort Santiago offers history and scenic river views'
      },
      {
        name: 'Cebu and Bohol',
        description: 'Chocolate Hills, island-hopping, whale shark encounters (seasonal).',
        tip: 'Combine with beach resorts for adventure and relaxation'
      }
    ],
    tips: [
      'Filipinos are very friendly - they love visitors and will help',
      'English is widely spoken - communication is easy',
      'Food is tasty - try adobo, sinigang, lechon, and fresh seafood',
      'Island hopping is affordable and spectacular',
      'Scuba diving is world-class and budget-friendly',
      'Typhoon season (June-November) - typhoons can disrupt travel',
      'Public transport is colorful tricycles, jeepneys, and buses',
      'Tipping is appreciated but not mandatory'
    ],
    faqs: [
      {
        question: 'How many days do you need in the Philippines?',
        answer: 'Ten to fourteen days, because the country is spread across more than 7,000 islands and internal transfers consume real time. A week realistically covers one region such as Palawan or the Cebu and Bohol pairing, not both.'
      },
      {
        question: 'What is the best time to visit the Philippines?',
        answer: 'December to May is the dry season, with March to May the hottest. Typhoon season runs roughly June to November and most affects the eastern seaboard and northern Luzon. Palawan and the Visayas are generally more sheltered than Manila.'
      },
      {
        question: 'Palawan or Cebu - which should you choose?',
        answer: 'Palawan for dramatic limestone scenery, El Nido island hopping and the Coron wrecks. Cebu and Bohol for easier logistics, waterfalls, whale sharks and better road connections. Palawan is the more striking landscape; Cebu is the simpler trip.'
      },
      {
        question: 'How do you travel between the islands?',
        answer: 'Domestic flights through Manila or Cebu are the practical backbone, supplemented by fast ferries for shorter hops. Build buffer time into island days, since ferry cancellations from weather are routine and a missed connection can cost a full day.'
      },
      {
        question: 'Is the Philippines good for diving beginners?',
        answer: 'It is one of the best places in the world to learn. Warm clear water, abundant certification centres and sites at every difficulty level make it accessible, with Moalboal, Coron and Apo Reef among the most popular. Courses cost considerably less than in Europe or Australia.'
      },
      {
        question: 'How long can Indians stay in the Philippines without a visa?',
        answer: 'Fourteen days for all Indian passport holders, or thirty days if you hold a valid visa or residence permit from Australia, Japan, the USA, Canada, the Schengen Area, Singapore or the UK. Neither can be extended once you have arrived, so plan the itinerary around fourteen days unless you qualify.'
      },
      {
        question: 'How much does a Philippines trip cost from India?',
        answer: 'On the ground it is inexpensive, but the country is spread across thousands of islands and internal transport dominates the budget. Domestic flights, ferries and island-hopping boat tours typically cost more than accommodation over a two week trip.'
      },
      {
        question: 'When is the cheapest time to visit the Philippines?',
        answer: 'June to November is low season because it coincides with the typhoon window, and rates fall accordingly. Palawan and the Visayas are more sheltered than Manila and northern Luzon, so the risk is uneven. December to May costs more but is far more reliable.'
      },
      {
        question: 'How long is the flight from India to the Philippines?',
        answer: 'Most routings connect through Singapore, Kuala Lumpur, Bangkok or Hong Kong, putting Manila roughly nine to twelve hours away in total. Since you will usually change planes anyway, it is worth checking fares to Cebu directly rather than routing everything through Manila.'
      },
      {
        question: 'What costs more than expected in the Philippines?',
        answer: 'Island-hopping tours in El Nido and Coron, which are priced per person per day and quickly become the largest line item; domestic flights booked late; and the environmental fees and terminal charges levied separately at many islands and ports.'
      }
    ]
  },
  'maldives': {
    id: 'maldives',
    name: 'Maldives',
    image: 'https://images.unsplash.com/photo-1514282401047-d79a71a590e8?w=1200&q=80',
    bestTime: 'November to April',
    currency: 'Maldivian Rufiyaa (MVR), USD accepted everywhere',
    language: 'Dhivehi, English widely spoken in tourism',
    climate: 'Tropical, 26-31°C year round with high humidity',
    tagline: 'Overwater villas, whale sharks, and the clearest water you will ever swim in',
    overview: 'The Maldives is a nation of 1,192 coral islands spread across 26 atolls, and it has become the most popular honeymoon destination for Indian travellers for good reason: it is a short flight away and the water is genuinely as clear as the photographs suggest. Since guesthouses were legalised on local islands in 2009, the country has opened up dramatically. You can now choose between a private-island resort where everything is included and a clean guesthouse on an island like Maafushi or Dhigurah for a fraction of the cost, snorkelling the same reefs either way. The marine life is the real draw. Whale sharks patrol South Ari Atoll all year, manta rays gather in numbers at Hanifaru Bay between May and November, and most islands have a house reef you can swim to straight from the beach without booking a boat.',
    visa: {
      summary: 'Free 30-day visa on arrival for all nationalities including India. No advance application needed.',
      details: [
        'Free 30-day tourist visa issued on arrival to every passport holder',
        'Passport must be valid for at least one month beyond arrival',
        'Confirmed accommodation booking is required and checked',
        'Return or onward ticket required',
        'Traveller Declaration form must be submitted online within 96 hours before arrival'
      ],
      note: 'Extensions up to 90 days are possible through Maldives Immigration once you are in the country'
    },
    packing: {
      'Clothing': ['Swimwear and rash guard', 'Light cotton and linen clothing', 'Modest cover-up for local islands (shoulders and knees)', 'Wide-brim hat', 'Flip flops and reef shoes'],
      'Accessories': ['Reef-safe sunscreen SPF 50+', 'Snorkel mask if you have your own', 'Dry bag for boat transfers', 'Sunglasses with strap', 'Refillable water bottle'],
      'Electronics': ['Waterproof phone case', 'Action camera', 'Universal adapter (Type D and G sockets)', 'Power bank for long boat days']
    },
    attractions: [
      {
        name: 'South Ari Atoll Whale Sharks',
        description: 'One of the few places on earth where whale sharks are present year-round, and you snorkel alongside them rather than dive.',
        tip: 'Book through a local guesthouse - the boats are often the same ones resorts use at triple the price'
      },
      {
        name: 'Hanifaru Bay, Baa Atoll',
        description: 'A UNESCO Biosphere Reserve where dozens of manta rays and occasionally whale sharks feed together in a shallow bay.',
        tip: 'Season runs May to November; snorkelling only, no diving permitted, and permits are limited'
      },
      {
        name: 'House Reef Snorkelling',
        description: 'Most islands sit on their own reef, so world-class coral and reef fish are a short swim from the sand.',
        tip: 'Ask which side of the island the reef drop-off is on and always check the current before entering'
      },
      {
        name: 'Maafushi and Local Islands',
        description: 'Inhabited islands with guesthouses, local cafes and a designated tourist beach, offering the Maldives at a fifth of resort prices.',
        tip: 'Public ferries are cheapest but do not run on Fridays; speedboat transfers run daily'
      },
      {
        name: 'Bioluminescent Plankton',
        description: 'On certain nights the shoreline glows blue as waves disturb bioluminescent plankton, most famously on Vaadhoo.',
        tip: 'Best seen on dark moonless nights away from any artificial light'
      },
      {
        name: 'Sandbank Picnic',
        description: 'A boat drops you on a bare strip of sand in the middle of the ocean with nothing but water in every direction.',
        tip: 'Go at low tide and bring shade - there is none at all out there'
      }
    ],
    tips: [
      'Split your trip between a local island and a resort for the best value and a fuller picture',
      'Seaplanes only fly in daylight, so late arrivals into Male mean an extra night near the airport',
      'Alcohol is illegal on local islands and only served at resorts and licensed floating bars',
      'Dress modestly on local islands except at the marked bikini beach',
      'Reef-safe sunscreen matters here and is required at many resorts',
      'Resort extras are charged to your room and add up fast - confirm what your board plan covers',
      'USD is accepted everywhere, so there is little need to carry rufiyaa',
      'Ramadan affects opening hours and dining on local islands'
    ],
    faqs: [
      {
        question: 'How many days do you need in the Maldives?',
        answer: 'Five to seven days including travel. Transfers eat into the trip, since seaplanes only fly in daylight and can mean an overnight in Malé depending on your flight times. Anything shorter than four nights leaves you barely settled before departure.'
      },
      {
        question: 'Can you visit the Maldives on a budget?',
        answer: 'Yes. Guesthouses on inhabited local islands such as Maafushi, Dhigurah and Thulusdhoo cost a small fraction of resort rates and are reached by public ferry or speedboat rather than seaplane. You trade the private-island experience for a far lower price and a closer look at Maldivian life.'
      },
      {
        question: 'What is the difference between a resort island and a local island?',
        answer: 'Resort islands are private, permit alcohol and bikinis anywhere, and include everything at a premium. Local islands are inhabited communities where Islamic law applies, so alcohol is unavailable and modest dress is expected away from designated bikini beaches. Local islands cost far less and many travellers combine both.'
      },
      {
        question: 'What is the best time to visit the Maldives?',
        answer: 'November to April is the dry northeast monsoon with calm seas and the best underwater visibility. May to October brings more rain and wind but also lower rates, and it is the reliable season for whale sharks and manta rays in South Ari Atoll.'
      },
      {
        question: 'Do you need to be able to swim?',
        answer: 'Not necessarily, but it transforms the trip. Most of what makes the Maldives special is underwater. Resorts and guesthouses offer lessons and guided snorkel trips with flotation aids, and house reefs are usually reachable from the beach.'
      },
      {
        question: 'How much does a Maldives trip cost from India?',
        answer: 'The gap between the two ways of visiting is enormous. A private resort island with a seaplane transfer is among the most expensive trips available; a local island guesthouse reached by public ferry costs a small fraction of it. Transfers alone can rival several nights of accommodation.'
      },
      {
        question: 'When is the cheapest time to visit the Maldives?',
        answer: 'May to October, the southwest monsoon, when rain and wind are more frequent and rates drop substantially. It is also the reliable season for whale sharks and manta rays in South Ari Atoll, so the trade-off suits anyone coming primarily to get in the water.'
      },
      {
        question: 'How long is the flight from India to the Maldives?',
        answer: 'Unusually short. Roughly an hour and a half from Kochi, around two from Bengaluru, under three from Mumbai and about four from Delhi, with direct flights from several Indian cities. It is far closer than most people assume.'
      },
      {
        question: 'What costs more than expected in the Maldives?',
        answer: 'Seaplane and speedboat transfers, which are charged per person and are unavoidable once you commit to a particular island. Resort food and drink are the other shock, since there is nowhere else to eat, which is why all-inclusive packages often work out cheaper than they look.'
      }
    ]
  },
  'seychelles': {
    id: 'seychelles',
    name: 'Seychelles',
    image: 'https://images.unsplash.com/photo-1610641818989-c2051b5e2cfd?w=1200&q=80',
    bestTime: 'April to May and October to November',
    currency: 'Seychellois Rupee (SCR)',
    language: 'Seychellois Creole, English and French',
    climate: 'Tropical, 26-30°C year round with two monsoon winds',
    tagline: 'Granite boulder beaches, rare wildlife, and Creole island life across 115 islands',
    overview: 'Seychelles is the only mid-ocean granite archipelago in the world, and that geology is what makes it look like nowhere else. The enormous weathered boulders at Anse Source d\'Argent on La Digue sit on white sand above shallow turquoise water, and behind the beaches the islands rise into hills, rainforest and waterfalls rather than flattening into coral atolls. Most visitors move between three islands: Mahe for the capital and airport, Praslin for the UNESCO-listed Vallee de Mai palm forest, and La Digue where bicycles outnumber cars. Nearly half the country is protected national park, giant Aldabra tortoises wander freely, and the coco de mer palm produces the largest seed of any plant on earth. It is expensive because almost everything is imported, but self-catering guesthouses and ferries instead of flights bring it within reach.',
    visa: {
      summary: 'No visa required for any nationality. A visitor permit is issued on arrival, valid up to 30 days.',
      details: [
        'Visa-free for all passports including India - a rarity',
        'Travel Authorisation must be applied for online before departure',
        'Proof of confirmed accommodation is required',
        'Return or onward ticket required',
        'Passport valid for the duration of stay'
      ],
      note: 'The online Travel Authorisation carries a fee and must be approved before you board your flight'
    },
    packing: {
      'Clothing': ['Swimwear and quick-dry clothing', 'Light hiking clothes for trails', 'Rash guard for snorkelling', 'Light rain jacket', 'Sandals plus trainers for hiking'],
      'Accessories': ['Reef-safe sunscreen', 'Insect repellent for forest trails', 'Daypack for island hopping', 'Snorkel gear if you have it', 'Reusable water bottle'],
      'Electronics': ['UK-style Type G adapter', 'Waterproof camera', 'Power bank', 'Head torch for early beach starts']
    },
    attractions: [
      {
        name: 'Anse Source d\'Argent, La Digue',
        description: 'The most photographed beach in the world, where giant granite boulders frame shallow, glass-clear water.',
        tip: 'Arrive at opening time and at low tide - by mid-morning both the light and the crowds have peaked'
      },
      {
        name: 'Vallee de Mai, Praslin',
        description: 'A UNESCO World Heritage palm forest that feels prehistoric, home to the coco de mer and the Seychelles black parrot.',
        tip: 'Go early for birdlife and cooler air; the whole loop takes about two hours'
      },
      {
        name: 'Anse Lazio and Anse Georgette',
        description: 'Two beaches on Praslin that regularly appear in global top-ten lists, both backed by granite headlands.',
        tip: 'Anse Georgette requires crossing a resort property - call ahead to arrange access'
      },
      {
        name: 'Cycling La Digue',
        description: 'An island with almost no cars where a bicycle is the practical way to reach every beach and village.',
        tip: 'Rent from the jetty on arrival; a few dollars a day and no booking needed'
      },
      {
        name: 'Curieuse Island Tortoises',
        description: 'A short boat ride from Praslin, home to free-roaming giant Aldabra tortoises and a mangrove boardwalk.',
        tip: 'Combine with a St Pierre snorkelling stop on the same day trip'
      },
      {
        name: 'Morne Seychellois National Park',
        description: 'Mahe\'s mountainous interior with rainforest trails, viewpoints and endemic plants found nowhere else.',
        tip: 'Copolia trail is the most rewarding half-day hike and needs no guide'
      }
    ],
    tips: [
      'Book self-catering guesthouses and shop at Victoria market to control costs',
      'Inter-island ferries are frequent and far cheaper than domestic flights - book ahead in season',
      'Southeast trade winds from June to September bring seaweed to east-facing beaches',
      'Tap water is safe on Mahe',
      'Carry cash for roadside stalls and small vendors',
      'Never touch or stand on coral - marine park rules are enforced',
      'Driving is on the left and roads on Mahe are narrow and steep',
      'Sundays are quiet with limited shop opening hours'
    ],
    faqs: [
      {
        question: 'How many days do you need in Seychelles?',
        answer: 'Seven to ten days to give Mahé, Praslin and La Digue a fair share each. The inter-island ferries are quick and reliable, so splitting a week across all three is realistic rather than ambitious.'
      },
      {
        question: 'Is Seychelles affordable?',
        answer: 'It has a reputation as a luxury destination, but self-catering guesthouses on all three main islands cost a fraction of the resorts. Buying groceries, using the cheap public buses on Mahé and cycling on La Digue brings the trip within reach of a mid-range budget.'
      },
      {
        question: 'Which Seychelles island should you stay on?',
        answer: 'Mahé has the airport, the widest choice of beaches and the only real town. Praslin has Anse Lazio and the Vallée de Mai palm forest. La Digue is the smallest and most atmospheric, with bicycles instead of cars and Anse Source d\'Argent. Most itineraries use two or three as bases.'
      },
      {
        question: 'What is the best time to visit Seychelles?',
        answer: 'April to May and October to November sit between the two trade wind seasons, giving the calmest seas and clearest water. The southeast trades from May to September bring wind and seaweed to some beaches, while December to March is hotter and wetter.'
      },
      {
        question: 'Do you need to hire a car in Seychelles?',
        answer: 'On Mahé a car makes reaching the quieter southern beaches much easier, though buses are cheap and cover the island. Praslin is manageable by bus or taxi. La Digue has almost no cars at all, and a bicycle is the standard way to get around.'
      },
      {
        question: 'How much does a Seychelles trip cost from India?',
        answer: 'Its luxury reputation is only half the story. Self-catering guesthouses exist on all three main islands and cost a fraction of the resorts, and groceries plus the cheap public buses on Mahé bring the daily spend within mid-range reach. Inter-island ferries are the fixed cost to plan around.'
      },
      {
        question: 'When is the cheapest time to visit Seychelles?',
        answer: 'The trade wind seasons, roughly June to August and December to February, are cheaper than the calm shoulder windows. The southeast trades bring wind and seaweed to some beaches, while December to March is hotter and wetter, so the saving comes with a genuine trade-off.'
      },
      {
        question: 'How long is the flight from India to Seychelles?',
        answer: 'Around five hours direct from Mumbai. From other Indian cities you will usually connect through Mumbai, Dubai, Doha or Abu Dhabi, which adds several hours but often costs less than the direct routing.'
      },
      {
        question: 'What costs more than expected in Seychelles?',
        answer: 'Groceries and eating out, since almost everything is imported and priced accordingly; inter-island ferries, which are efficient but not cheap; and car hire on Mahé, which is limited in supply and rises steeply if you book close to your dates.'
      }
    ]
  },
  'mauritius': {
    id: 'mauritius',
    name: 'Mauritius',
    image: 'https://images.unsplash.com/photo-1520250497591-112f2f40a3f4?w=1200&q=80',
    bestTime: 'May to December',
    currency: 'Mauritian Rupee (MUR)',
    language: 'Mauritian Creole, English and French',
    climate: 'Tropical, 22-30°C with cyclone season January to March',
    tagline: 'Lagoon beaches, volcanic peaks, and an island where Indian heritage runs deep',
    overview: 'Mauritius is ringed almost entirely by a coral reef, which is why its lagoons are so calm and its beaches so swimmable, but the interior is what sets it apart from other island destinations. Volcanic peaks, sugar cane fields, waterfalls and the forests of Black River Gorges give you somewhere to go when you have had enough of the sand. Around two-thirds of Mauritians are of Indian descent, and Hindu temples, Tamil festivals and Bhojpuri songs sit alongside Creole, French and Chinese traditions. For Indian travellers that creates an unusual mix of the familiar and the foreign, from dholl puri sold on street corners to the sacred crater lake at Ganga Talao. Infrastructure is strong, roads are good, English and French are widely spoken, and visa on arrival is free, which makes it one of the easiest long-haul beach destinations to organise.',
    visa: {
      summary: 'Free visa on arrival for most nationalities including India, valid up to 90 days.',
      details: [
        'Indian nationals receive a free 90-day visa on arrival',
        'Passport valid for at least six months beyond arrival',
        'Proof of accommodation and sufficient funds may be requested',
        'Return or onward ticket required',
        'All arrivals must complete the Mauritius All-in-One Travel form online before departure'
      ],
      note: 'The online arrival form covers immigration, customs and health declarations in one submission'
    },
    packing: {
      'Clothing': ['Swimwear and beach cover-ups', 'Light layers for cooler winter evenings (May to September)', 'Modest clothing for temple visits', 'Hiking shoes for Black River Gorges', 'Light rain jacket'],
      'Accessories': ['Sunscreen and hat', 'Insect repellent', 'Snorkel gear', 'Daypack', 'Reusable water bottle'],
      'Electronics': ['Type C and G adapter', 'Camera', 'Power bank', 'Phone mount if renting a car']
    },
    attractions: [
      {
        name: 'Le Morne Brabant',
        description: 'A UNESCO-listed basalt mountain rising straight out of the lagoon, once a refuge for escaped slaves.',
        tip: 'The guided summit hike takes about four hours return and starts early to beat the heat'
      },
      {
        name: 'Chamarel Seven Coloured Earths',
        description: 'Dunes of volcanic sand in seven distinct shades, alongside a hundred-metre waterfall and rum distillery.',
        tip: 'Visit in morning light when the colour separation is sharpest'
      },
      {
        name: 'Ganga Talao (Grand Bassin)',
        description: 'A sacred crater lake and major Hindu pilgrimage site, watched over by a towering Shiva statue.',
        tip: 'An active place of worship - dress modestly and remove shoes at the temples'
      },
      {
        name: 'Black River Gorges National Park',
        description: 'The island\'s largest protected forest, with waterfalls, endemic birds and viewpoints across the southwest.',
        tip: 'Macchabee trail gives the best views; go after rain for the waterfalls'
      },
      {
        name: 'Blue Bay Marine Park',
        description: 'A protected lagoon with exceptional coral, reachable by glass-bottom boat or straight off the beach.',
        tip: 'Snorkel early before boat traffic stirs up the sand'
      },
      {
        name: 'Port Louis Central Market',
        description: 'The capital\'s working market for fruit, spices, textiles and the best dholl puri on the island.',
        tip: 'Go in the morning and eat where the queue is longest'
      }
    ],
    tips: [
      'Rent a car - the island is small, roads are good, and the interior is worth the freedom',
      'Driving is on the left, familiar for Indian and UK visitors',
      'Cyclone season runs January to March, so plan around it',
      'The south coast around Gris Gris has open sea and dangerous currents, unlike the sheltered lagoons',
      'Street food is excellent and safe - dholl puri from a busy cart is the classic cheap meal',
      'Ganga Talao is a pilgrimage site, not a viewpoint - dress and behave accordingly',
      'Book catamaran and Ile aux Cerfs trips through your guesthouse rather than beach touts',
      'Sundays are quiet with many shops closed'
    ],
    faqs: [
      {
        question: 'How many days do you need in Mauritius?',
        answer: 'Seven days is the sweet spot, giving you beach time on two coasts plus the interior with Chamarel, Black River Gorges and the tea route. The island is small enough that a single base works, though moving once between the north and southwest adds variety.'
      },
      {
        question: 'What is the best time to visit Mauritius?',
        answer: 'May to December is the cooler, drier half of the year and the most comfortable for exploring. January to March is hot and humid and falls within the cyclone season. Diving visibility is best from November to April.'
      },
      {
        question: 'Is Mauritius only a honeymoon destination?',
        answer: 'That is its reputation, but the island works just as well for families and for travellers who want more than a resort. Hiking Le Morne, the Seven Coloured Earths, Port Louis market, the Ganga Talao temple and the sega music scene give the trip real substance beyond the beach.'
      },
      {
        question: 'Which part of Mauritius should you stay in?',
        answer: 'The north around Grand Baie has the liveliest restaurants and nightlife, the west around Flic en Flac has sunsets and dolphin trips, the east is quieter with the best lagoons, and the southwest near Le Morne suits hiking and kitesurfing. Roads are good, so nothing is far.'
      },
      {
        question: 'Is Mauritius easy for Indian travellers?',
        answer: 'Unusually so. A large share of the population is of Indian origin, Hindi and Bhojpuri are widely understood, Hindu temples are common, and Indian food is available everywhere. Many travellers describe it as familiar and foreign at the same time.'
      },
      {
        question: 'How much does a Mauritius trip cost from India?',
        answer: 'Mauritius spans a wide range. All-inclusive beach resorts drive most package prices, but self-catering apartments and guesthouses are widely available and cost far less. Because the island is small and roads are good, transport barely registers compared with island destinations that need boats or internal flights.'
      },
      {
        question: 'When is the cheapest time to visit Mauritius?',
        answer: 'January to March is the low season, being hot, humid and within the cyclone window, and rates fall accordingly. The trade-off is real but the risk is often overstated. May and June offer much of the saving with far more reliable weather.'
      },
      {
        question: 'How long is the flight from India to Mauritius?',
        answer: 'Roughly six hours direct from Mumbai and around seven from Delhi, with direct services on several routes. Connections through Dubai or Doha are also common and can be cheaper, though they add a substantial amount of time.'
      },
      {
        question: 'What costs more than expected in Mauritius?',
        answer: 'Resort dining, where being on a private beach usually means eating on site at resort prices; catamaran and dolphin excursions, which are the standard day trips and priced for tourists; and car hire, which is worth it but adds up over a week.'
      }
    ]
  },
  'turkey': {
    id: 'turkey',
    name: 'Turkey',
    image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?w=1200&q=80',
    bestTime: 'April to June and September to October',
    currency: 'Turkish Lira (TRY)',
    language: 'Turkish, English in tourist areas',
    climate: 'Varies widely - hot dry summers on the coast, cold snowy winters in Cappadocia',
    tagline: 'Where Europe meets Asia: Ottoman mosques, balloons over Cappadocia, and a turquoise coast',
    overview: 'Turkey sits on the ground where Europe and Asia meet, and almost every civilisation that mattered left something standing. Istanbul was the capital of the Roman, Byzantine and Ottoman empires in turn, and it is the only major city spanning two continents, with commuter ferries crossing between them for the price of a bus ticket. Cappadocia in central Anatolia is a landscape of eroded volcanic rock hollowed into cave churches and underground cities, and at dawn hundreds of hot air balloons rise out of its valleys. The Aegean and Mediterranean coasts hold Ephesus and other exceptional classical ruins alongside a coastline of hidden bays explored by wooden gulet boats, while Pamukkale\'s white travertine terraces spill down a hillside above a Roman spa town. Turkey is also excellent value, with cheap domestic flights, a favourable exchange rate and a straightforward online e-visa.',
    visa: {
      summary: 'Online e-visa available for most nationalities including India. Approval is usually immediate.',
      details: [
        'Apply through the official e-visa portal before travelling',
        'Indian nationals holding a valid Schengen, UK, US or Irish visa qualify for the simplified e-visa',
        'Passport valid for at least six months beyond arrival',
        'Proof of accommodation and return ticket may be requested',
        'E-visa is typically valid for 30 days from date of entry'
      ],
      note: 'Use only the official government e-visa site - many lookalike sites charge large mark-ups'
    },
    packing: {
      'Clothing': ['Layers - Cappadocia mornings are cold even in summer', 'Scarf for mosque visits', 'Comfortable walking shoes for cobbled streets and ruins', 'Modest clothing covering shoulders and knees', 'Warm jacket if visiting between November and March'],
      'Accessories': ['Sunscreen and sunglasses', 'Daypack', 'Reusable water bottle', 'Small towel for Pamukkale terraces', 'Slip-on shoes for mosque entry'],
      'Electronics': ['Type C and F European adapter', 'Camera for balloon sunrise', 'Power bank', 'Offline maps downloaded']
    },
    attractions: [
      {
        name: 'Hagia Sophia and the Blue Mosque',
        description: 'Fifteen centuries of history facing each other across a square in Sultanahmet - cathedral, mosque, museum, mosque again.',
        tip: 'Both are working mosques; avoid prayer times and dress modestly with head covering for women'
      },
      {
        name: 'Cappadocia Balloons at Sunrise',
        description: 'Hundreds of balloons lift out of the fairy chimney valleys at dawn, one of the great sights in travel.',
        tip: 'Flights are cancelled in high wind - allow at least two mornings in Cappadocia, not one'
      },
      {
        name: 'Goreme Open Air Museum',
        description: 'A cluster of rock-cut Byzantine churches with frescoes still visible on the ceilings.',
        tip: 'The Dark Church costs extra and has the best preserved frescoes - it is worth it'
      },
      {
        name: 'Kaymakli and Derinkuyu Underground Cities',
        description: 'Multi-level cities carved into soft rock, complete with stables, chapels and ventilation shafts.',
        tip: 'Genuinely tight and low in places - skip if you are claustrophobic'
      },
      {
        name: 'Ephesus',
        description: 'One of the best preserved classical cities in the Mediterranean, including the facade of the Library of Celsus.',
        tip: 'Enter from the upper gate and walk downhill; go early to avoid cruise-ship crowds'
      },
      {
        name: 'Pamukkale Travertine Terraces',
        description: 'White mineral terraces filled with warm turquoise water, above the Roman ruins of Hierapolis.',
        tip: 'You must walk barefoot on the terraces - bring a bag for your shoes'
      }
    ],
    tips: [
      'Apply for the e-visa online in advance through the official portal only',
      'Use domestic flights for long hops - Istanbul to Cappadocia is an hour by air, twelve by bus',
      'Buy an Istanbulkart on arrival; it covers trams, ferries, metro and buses at lower fares',
      'Dress modestly in working mosques - scarves are usually provided at the entrance',
      'Bargaining is expected in bazaars but not in shops or restaurants',
      'The lira fluctuates so check current rates rather than trusting older guides',
      'Vegetarian food is easy to find thanks to the meze tradition',
      'Avoid tap water outside major cities and tip around ten percent in restaurants'
    ],
    faqs: [
      {
        question: 'How many days do you need in Turkey?',
        answer: 'Ten to twelve days covers Istanbul, Cappadocia and one coastal or classical stop such as Antalya, Pamukkale or Ephesus. Istanbul alone justifies four days. The country is large, so use domestic flights rather than long overland hops.'
      },
      {
        question: 'What is the best time to visit Turkey?',
        answer: 'April to June and September to October give warm days, manageable crowds and the best light for both Istanbul and Cappadocia. July and August are hot, especially inland and on the coast. Winter brings snow to Cappadocia, which is beautiful but grounds balloons more often.'
      },
      {
        question: 'How much does a Cappadocia hot air balloon ride cost and will it fly?',
        answer: 'Flights are weather dependent and are cancelled outright when wind or visibility fails safety limits, which happens regularly in winter and occasionally year-round. Book for your first morning in Cappadocia so a cancellation still leaves a second chance, and confirm the refund policy before paying.'
      },
      {
        question: 'Is Turkey expensive?',
        answer: 'The lira has weakened considerably, which makes food, transport and domestic flights good value for foreign visitors. Museums, guided tours and balloon flights are often priced in euros and have not fallen in the same way. Check current rates rather than relying on older guides.'
      },
      {
        question: 'Is Turkey suitable for vegetarian travellers?',
        answer: 'Yes, more than its kebab reputation suggests. The meze tradition is largely vegetable based, and dishes such as mercimek çorbası, imam bayıldı, and gozleme with cheese or spinach are everywhere. Ask whether stocks are meat based, as they sometimes are.'
      },
      {
        question: 'How much does a Turkey trip cost from India?',
        answer: 'The weak lira makes food, local transport and domestic flights good value for foreign visitors. What has not fallen are the things priced in euros: guided tours, museum passes and the Cappadocia balloon flight. Those three, plus internal flights, shape most of the budget.'
      },
      {
        question: 'When is the cheapest time to visit Turkey?',
        answer: 'November to March, outside the Christmas and New Year window. Istanbul works well in winter and is considerably cheaper, though Cappadocia sees snow and balloon flights are grounded more often. July and August are both the hottest and the most expensive months.'
      },
      {
        question: 'How long is the flight from India to Turkey?',
        answer: 'Around six and a half to seven and a half hours to Istanbul from Delhi and Mumbai, with direct services on both routes. Istanbul is also a major connecting hub for onward travel to Europe, which makes pairing the two straightforward.'
      },
      {
        question: 'What costs more than expected in Turkey?',
        answer: 'The Cappadocia balloon flight, which is priced in euros and is the single largest activity cost for most visitors; domestic flights, which are essential given the distances; and bazaar purchases, where the opening price bears little relation to what the item should cost.'
      }
    ]
  }
};

